import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Pagination,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHorizOutlined';
import { listIntakeTransfers } from '../../api/intakeTransfers';
import { useAuth } from '../../context/authContext';
import { PageHeader } from '../../components/PageHeader';
import { IntakeTransferRequest, IntakeTransferStatus } from '../../types/intakeTransfer';
import { ApproveTransferDialog, RejectTransferDialog } from './TransferDecisionDialogs';
import { TransferRequestCard } from './TransferRequestCard';
import { SUPER_ADMIN_ROLE, transferErrorMessage } from './transferUtils';

const PAGE_SIZE = 20;
type TabValue = IntakeTransferStatus | 'ALL';

/** CRM → Intake Transfers (Phase 94): every transfer request in the
 * viewer's scope. Only a Super Admin gets Approve/Reject (the server
 * enforces this too). `?request=<id>` (from a notification) highlights
 * that request. */
export const IntakeTransfersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role.name === SUPER_ADMIN_ROLE;
  const [searchParams] = useSearchParams();
  const highlightId = Number(searchParams.get('request')) || null;
  const [tab, setTab] = useState<TabValue>(highlightId ? 'ALL' : 'PENDING');
  const [page, setPage] = useState(1);
  const [approving, setApproving] = useState<IntakeTransferRequest | null>(null);
  const [rejecting, setRejecting] = useState<IntakeTransferRequest | null>(null);
  const highlightedRef = useRef<HTMLDivElement | null>(null);

  const query = useQuery({
    queryKey: ['intake-transfers', 'list', tab, page],
    queryFn: () =>
      listIntakeTransfers({ page, pageSize: PAGE_SIZE, status: tab === 'ALL' ? undefined : tab }),
  });

  useEffect(() => {
    highlightedRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [query.data, highlightId]);

  const requests = query.data?.data ?? [];
  const pageCount = Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE));

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={SwapHorizIcon}
        title="Intake Transfers"
        subtitle={
          isSuperAdmin
            ? 'Requests to move a student to another intake or batch. Approve to apply the change.'
            : 'Requests to move a student to another intake or batch. A Super Admin approves or rejects them.'
        }
      />

      <Tabs
        value={tab}
        onChange={(_e, v: TabValue) => {
          setTab(v);
          setPage(1);
        }}
        sx={{ mb: 2 }}
      >
        <Tab label="Pending" value="PENDING" />
        <Tab label="Approved" value="APPROVED" />
        <Tab label="Rejected" value="REJECTED" />
        <Tab label="All" value="ALL" />
      </Tabs>

      {query.isLoading ? (
        <CircularProgress />
      ) : query.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => query.refetch()}>
              Retry
            </Button>
          }
        >
          Couldn't load transfer requests: {transferErrorMessage(query.error)}
        </Alert>
      ) : requests.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {tab === 'PENDING'
            ? 'No transfer requests are waiting for approval.'
            : 'No transfer requests here.'}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {requests.map((r) => (
            <TransferRequestCard
              key={r.id}
              ref={r.id === highlightId ? highlightedRef : undefined}
              request={r}
              highlighted={r.id === highlightId}
              showStudent
              onOpenStudent={() =>
                navigate(`/students/${r.studentId}?tab=academic&transfer=${r.id}`)
              }
              onApprove={isSuperAdmin && r.status === 'PENDING' ? () => setApproving(r) : undefined}
              onReject={isSuperAdmin && r.status === 'PENDING' ? () => setRejecting(r) : undefined}
            />
          ))}
        </Stack>
      )}

      {pageCount > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination count={pageCount} page={page} onChange={(_e, p) => setPage(p)} />
        </Stack>
      )}

      <ApproveTransferDialog request={approving} onClose={() => setApproving(null)} />
      <RejectTransferDialog request={rejecting} onClose={() => setRejecting(null)} />
    </Box>
  );
};
